import { Cell, Line, Terminal } from "../interface"

export const GRID_SIZE = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]
const COLOR_LIST = ['red', 'DarkKhaki', 'green', 'blue', 'black',
    'brown', 'fuchsia', 'purple', 'navy', 'teal', 'olive',
    'pink', 'grey', 'orange', 'lime', 'sandybrown']
const DIR_X = [0, 0, 1, -1]
const DIR_Y = [1, -1, 0, 0]

export interface GameState {
    rowNumber: number
    columnNumber: number
    grid: Cell[][]
    lines: Line[]
    answerLines: Line[]
    temporaryLines: Line[]
    terminals: Terminal[]
}

export class GameGenerator {
    private rowNumber: number = 0
    private columnNumber: number = 0
    private grid: Cell[][] = []
    private lines: Line[] = []
    private terminals: Terminal[] = []

    private colorsLength: number[] = []
    private occupied: boolean[][] = []
    private directionPermutation: number[] = [0, 1, 2, 3]

    private getRandomInt = (min: number, max: number): number => {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    private getColorNumber = (): number =>
        Math.min(this.rowNumber, this.columnNumber) - 1

    private initializeGrid = (): void => {
        this.grid = []
        this.lines = []
        this.terminals = []
        this.directionPermutation = this.randomPermutation(4)
        this.occupied = []
        for (let i = 0; i < this.rowNumber; i++) {
            this.grid.push([])
            this.occupied.push([])
            for (let j = 0; j < this.columnNumber; j++) {
                this.grid[i].push({ row: i, column: j })
                this.occupied[i].push(false)
            }
        }
    }

    private initializeColorsLength = (): void => {
        const colorNumber = this.getColorNumber()
        const totalLineLength = this.rowNumber * this.columnNumber - colorNumber
        const averageLength = Math.floor(totalLineLength / colorNumber)

        for (let i = 0; i < colorNumber; i++) {
            this.colorsLength.push(averageLength)
            if (i === colorNumber - 1) {
                this.colorsLength[i] = totalLineLength - averageLength * i
            }
            if ((i % 2) === 1) {
                const random = this.getRandomInt(1, averageLength - 3)
                this.colorsLength[i] -= random
                this.colorsLength[i - 1] += random
            }
        }
    }

    private isCellCanBeOccupied =
        (graph: boolean[][], row: number, column: number): boolean => {
            if ((row < 0) || (row >= this.rowNumber) ||
                (column < 0) || column >= this.columnNumber) {
                return false
            }
            return !graph[row][column]
        }

    private getTerminalCells = (graph: boolean[][]): Cell[] => {
        const terminalCells: Cell[] = []
        for (let i = 0; i < this.rowNumber; i++) {
            for (let j = 0; j < this.columnNumber; j++) {
                if (graph[i][j]) continue
                let feasibleMove = 0;
                for (let k = 0; k < DIR_X.length; k++) {
                    const nextRow = i + DIR_X[k]
                    const nextColumn = j + DIR_Y[k]
                    if (this.isCellCanBeOccupied(graph, nextRow, nextColumn)) {
                        feasibleMove++
                    }
                }
                if (feasibleMove === 1) {
                    terminalCells.push({ row: i, column: j })
                }
            }
        }
        return terminalCells
    }

    private getRandomUnoccupiedCell = (graph: boolean[][]): Cell => {
        const cell: Cell = { row: -1, column: -1 }
        while (true) {
            const randomRow = this.getRandomInt(0, this.rowNumber - 1)
            const randomColumn = this.getRandomInt(0, this.columnNumber - 1)
            if (this.isCellCanBeOccupied(graph, randomRow, randomColumn)) {
                cell.row = randomRow
                cell.column = randomColumn
                break
            }
        }
        return cell
    }

    private dfs = (graph: boolean[][], cell: Cell) => {
        graph[cell.row][cell.column] = true
        for (let i = 0; i < DIR_X.length; i++) {
            const nextRow = cell.row + DIR_X[i]
            const nextColumn = cell.column + DIR_Y[i]
            if (this.isCellCanBeOccupied(graph, nextRow, nextColumn)) {
                const nextCell = { row: nextRow, column: nextColumn }
                this.dfs(graph, nextCell)
            }
        }
    }

    private isSingleConnectedComponent = (graph: boolean[][]) => {
        let numberComponent = 0
        for (let i = 0; i < this.rowNumber; i++)
            for (let j = 0; j < this.columnNumber; j++)
                if (!graph[i][j]) {
                    const nextCell = { row: i, column: j }
                    this.dfs(graph, nextCell)
                    numberComponent++
                    if (numberComponent > 1) return false
                }
        return true
    }

    private randomPermutation = (N: number): number[] => {
        //https://stackoverflow.com/a/14033199

        const arr = []
        for (let i = 0; i < N; i++) arr.push(i)
        var permutation = new Array(arr.length);
        for (let i = 0; i < arr.length; i++) {
            permutation[i] = arr[i]
        }

        // then proceed to shuffle the permutation array      
        for (var i = 0; i < permutation.length; i++) {
            var swpIdx = i + Math.floor(Math.random() * (permutation.length - i))
            // now swap elements at idx and swpIdx
            var tmp = permutation[i]
            permutation[i] = permutation[swpIdx]
            permutation[swpIdx] = tmp
        }
        // here permutation[] will have been randomly shuffled (permuted)
        return permutation
    }

    private connectVertices =
        (graph: boolean[][], cell: Cell, color: number, vertices: Cell[]): boolean | Cell[] => {
            graph[cell.row][cell.column] = true
            const colorLength = this.colorsLength[color]
            if ((vertices.length - 1) === colorLength) return vertices

            for (let i = 0; i < DIR_X.length; i++) {
                const nextRow = cell.row + DIR_X[this.directionPermutation[i]]
                const nextColumn = cell.column + DIR_Y[this.directionPermutation[i]]

                if (this.isCellCanBeOccupied(graph, nextRow, nextColumn)) {
                    let clonedGraph: boolean[][] = []
                    for (let j = 0; j < this.rowNumber; j++)
                        clonedGraph.push([...graph[j]])
                    clonedGraph[nextRow][nextColumn] = true

                    if (this.isSingleConnectedComponent(clonedGraph)) {
                        const nextCell = { row: nextRow, column: nextColumn }
                        const result = this.connectVertices(graph, nextCell, color,
                            vertices.concat(nextCell))
                        if (result !== false) {
                            return result
                        }
                    }
                }
            }

            graph[cell.row][cell.column] = false
            return false
        }

    setRowNumber = (rowNumber: number): GameGenerator => {
        this.rowNumber = rowNumber
        return this
    }

    setColumnNumber = (columnNumber: number): GameGenerator => {
        this.columnNumber = columnNumber
        return this
    }

    generate = (): GameState => {
        this.initializeColorsLength()
        let repeat = true
        let result: GameState = {} as GameState

        while (repeat) {
            this.initializeGrid()

            repeat = false
            const colorNumber = this.getColorNumber()
            for (let i = 0; i < colorNumber; i++) {
                const terminalCells = this.getTerminalCells(this.occupied)
                const startCell: Cell = terminalCells.length ?
                    terminalCells[this.getRandomInt(0, terminalCells.length - 1)] :
                    this.getRandomUnoccupiedCell(this.occupied)
                const vertices = this.connectVertices(this.occupied,
                    startCell, i, [startCell]) as Cell[]

                if (!vertices) {
                    repeat = true
                }

                this.terminals.push({
                    cell: vertices[0],
                    color: COLOR_LIST[i]
                }, {
                    cell: vertices[vertices.length - 1],
                    color: COLOR_LIST[i]
                })

                for (let j = 1; j < vertices.length; j++) {
                    this.lines.push({
                        source: vertices[j - 1],
                        destination: vertices[j],
                        color: COLOR_LIST[i]
                    })
                }
            }
            if (repeat) continue

            result = {
                rowNumber: this.rowNumber,
                columnNumber: this.columnNumber,
                grid: this.grid,
                terminals: this.terminals,
                answerLines: this.lines,
                lines: [],
                temporaryLines: []
            }
        }
        return result
    }
}
